import { resolveSportOutcomeSlots } from '@/features/market/utils/sportOutcomeSlots'

// FIFA trigram -> ISO 3166-1 alpha-2 (plus gb-* subdivisions) used by circle-flags
const FIFA_TO_ISO = {
  AFG: 'af', ALB: 'al', ALG: 'dz', AND: 'ad', ANG: 'ao', AIA: 'ai', ATG: 'ag',
  ARG: 'ar', ARM: 'am', ARU: 'aw', ASA: 'as', AUS: 'au', AUT: 'at', AZE: 'az',
  BAH: 'bs', BHR: 'bh', BAN: 'bd', BRB: 'bb', BLR: 'by', BEL: 'be', BLZ: 'bz',
  BEN: 'bj', BER: 'bm', BHU: 'bt', BOL: 'bo', BIH: 'ba', BOT: 'bw', BRA: 'br',
  VGB: 'vg', BRU: 'bn', BUL: 'bg', BFA: 'bf', BDI: 'bi', CPV: 'cv', CAM: 'kh',
  CMR: 'cm', CAN: 'ca', CAY: 'ky', CTA: 'cf', CHA: 'td', CHI: 'cl', CHN: 'cn',
  TPE: 'tw', COL: 'co', COM: 'km', CGO: 'cg', COD: 'cd', COK: 'ck', CRC: 'cr',
  CIV: 'ci', CRO: 'hr', CUB: 'cu', CUW: 'cw', CYP: 'cy', CZE: 'cz', DEN: 'dk',
  DJI: 'dj', DMA: 'dm', DOM: 'do', ECU: 'ec', EGY: 'eg', SLV: 'sv', ENG: 'gb-eng',
  EQG: 'gq', ERI: 'er', EST: 'ee', SWZ: 'sz', ETH: 'et', FRO: 'fo', FIJ: 'fj',
  FIN: 'fi', FRA: 'fr', GAB: 'ga', GAM: 'gm', GEO: 'ge', GER: 'de', GHA: 'gh',
  GIB: 'gi', GRE: 'gr', GRN: 'gd', GUM: 'gu', GUA: 'gt', GUI: 'gn', GNB: 'gw',
  GUY: 'gy', HAI: 'ht', HON: 'hn', HKG: 'hk', HUN: 'hu', ISL: 'is', IND: 'in',
  IDN: 'id', IRN: 'ir', IRQ: 'iq', NIR: 'gb-nir', ISR: 'il', ITA: 'it', JAM: 'jm',
  JPN: 'jp', JOR: 'jo', KAZ: 'kz', KEN: 'ke', KOS: 'xk', KUW: 'kw', KGZ: 'kg',
  LAO: 'la', LVA: 'lv', LIB: 'lb', LES: 'ls', LBR: 'lr', LBY: 'ly', LIE: 'li',
  LTU: 'lt', LUX: 'lu', MAC: 'mo', MAD: 'mg', MWI: 'mw', MAS: 'my', MDV: 'mv',
  MLI: 'ml', MLT: 'mt', MTN: 'mr', MRI: 'mu', MEX: 'mx', MDA: 'md', MNG: 'mn',
  MNE: 'me', MSR: 'ms', MAR: 'ma', MOZ: 'mz', MYA: 'mm', NAM: 'na', NEP: 'np',
  NED: 'nl', NCL: 'nc', NZL: 'nz', NCA: 'ni', NIG: 'ne', NGA: 'ng', MKD: 'mk',
  NOR: 'no', OMA: 'om', PAK: 'pk', PLE: 'ps', PAN: 'pa', PNG: 'pg', PAR: 'py',
  PER: 'pe', PHI: 'ph', POL: 'pl', POR: 'pt', PUR: 'pr', QAT: 'qa', IRL: 'ie',
  ROU: 'ro', RUS: 'ru', RWA: 'rw', SKN: 'kn', LCA: 'lc', VIN: 'vc', SAM: 'ws',
  SMR: 'sm', STP: 'st', KSA: 'sa', SCO: 'gb-sct', SEN: 'sn', SRB: 'rs', SEY: 'sc',
  SLE: 'sl', SGP: 'sg', SVK: 'sk', SVN: 'si', SOL: 'sb', SOM: 'so', RSA: 'za',
  KOR: 'kr', PRK: 'kp', SSD: 'ss', ESP: 'es', SRI: 'lk', SDN: 'sd', SUR: 'sr',
  SWE: 'se', SUI: 'ch', SYR: 'sy', TAH: 'pf', TJK: 'tj', TAN: 'tz', THA: 'th',
  TLS: 'tl', TOG: 'tg', TGA: 'to', TRI: 'tt', TUN: 'tn', TUR: 'tr', TKM: 'tm',
  TCA: 'tc', UGA: 'ug', UKR: 'ua', UAE: 'ae', USA: 'us', URU: 'uy', UZB: 'uz',
  VAN: 'vu', VEN: 've', VIE: 'vn', WAL: 'gb-wls', YEM: 'ye', ZAM: 'zm', ZIM: 'zw',
}

// Prefer backend logos from event_extra_data (SportMonks crests, etc.).
// CDN country flags are only a last resort — club short codes like GRE
// collide with FIFA country codes (Greece) and show the wrong image.
export const getTeamFlagUrl = (shortCode, fallbackLogo = null) => {
  if (fallbackLogo) return fallbackLogo
  const iso = FIFA_TO_ISO[shortCode?.toUpperCase?.()]
  if (!iso) return null
  return `https://cdn.jsdelivr.net/gh/hatscripts/circle-flags/flags/${iso}.svg`
}

// Rectangular flag (for the WC-style rounded-rect fixture flags); same
// preference order as getTeamFlagUrl.
export const getTeamRectFlagUrl = (shortCode, fallbackLogo = null) => {
  if (fallbackLogo) return fallbackLogo
  const iso = FIFA_TO_ISO[shortCode?.toUpperCase?.()]
  if (!iso) return null
  return `https://flagcdn.com/w80/${iso}.png`
}

/**
 * The image a side is drawn with: its crest when the feed has one, else its own outcome's
 * thumbnail, else the market's — the disc the backend gives every outcome by default — so
 * no side is ever blank.
 */
export const getTeamSideImage = (question, side) => {
  const data = question?.event_extra_data || {}
  const crest = getTeamFlagUrl(data[`${side}_team_short_code`], data[`${side}_team_logo`])
  if (crest) return crest
  const outcomes = question?.outcomes ?? []
  /* One outcome carries the market's default image, shared by both sides. With one per
     side, a side whose outcome cannot be told apart takes the market's image rather than
     another side's crest. */
  const sideOutcome = outcomes.length > 1 ? resolveSportOutcomeSlots(outcomes, data)[side] : outcomes[0]
  return sideOutcome?.thumbnail || question?.thumbnail || null
}
